import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../lib/supabase';

const BusinessContext = createContext(null);

const ACTIVE_BUSINESS_KEY = '@slate_active_business_id';

export function BusinessProvider({ children, userId }) {
  const [businesses, setBusinesses] = useState([]);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBusinesses = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      setBusinesses(data || []);

      // Restore previously active business, fall back to default
      const storedId = await AsyncStorage.getItem(ACTIVE_BUSINESS_KEY);
      const stored = data?.find(b => b.id === storedId);
      const defaultBiz = data?.find(b => b.is_default) || data?.[0];
      setActiveBusiness(stored || defaultBiz || null);
    } catch (e) {
      console.warn('BusinessContext fetch error:', e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  const switchBusiness = useCallback(async (business) => {
    setActiveBusiness(business);
    await AsyncStorage.setItem(ACTIVE_BUSINESS_KEY, business.id);
  }, []);

  const createBusiness = useCallback(async ({ name, trade_type, business_type, phone, email, address }) => {
    const { data, error } = await supabase
      .from('businesses')
      .insert({
        user_id: userId,
        name,
        trade_type: trade_type || '',
        business_type: business_type || '',
        phone: phone || '',
        email: email || '',
        address: address || '',
        default_markup: 20,
        default_tax_rate: 8.5,
        payment_terms: 'Net 30',
        is_default: false,
      })
      .select()
      .single();
    if (error) throw error;
    setBusinesses(prev => [...prev, data]);
    return data;
  }, [userId]);

  const updateBusiness = useCallback(async (id, updates) => {
    const { data, error } = await supabase
      .from('businesses')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();
    if (error) throw error;
    setBusinesses(prev => prev.map(b => b.id === id ? data : b));
    setActiveBusiness(prev => prev?.id === id ? data : prev);
    return data;
  }, [userId]);

  return (
    <BusinessContext.Provider value={{
      businesses,
      activeBusiness,
      loading,
      switchBusiness,
      createBusiness,
      updateBusiness,
      refetch: fetchBusinesses,
    }}>
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusiness must be used within BusinessProvider');
  return ctx;
}
